import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../supabaseClient";
import Layout from "../../components/Layout";
import { motion } from "framer-motion";
import { scopedTransactionSelect, scopedTransactionUpdate } from '../../utils/secureQuery';
import { parseUuid, sanitizeText, validatePersonName } from '../../utils/inputValidation';

// --- Componente de Logo (Con lógica de iconos de respaldo) ---
const MethodLogo = ({ methodName, isSelected, size = "large" }) => {
  const [hasError, setHasError] = useState(false);

  const formattedName = methodName?.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, '-') || 'default';
    
  const imagePath = `/logos/${formattedName}.png`;
  const sizeClasses = size === "large" ? "w-14 h-14" : "w-12 h-12";
  const iconSize = size === "large" ? "w-6 h-6" : "w-5 h-5";

  const getFallbackIcon = () => {
    const name = methodName?.toLowerCase() || '';
    if (name.includes("transferencia") || name.includes("banco") || name.includes("pichincha")) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
        </svg>
      );
    }
    if (name.includes("movil") || name.includes("zelle") || name.includes("nequi") || name.includes("yape") || name.includes("plata") || name.includes("plin")) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
          <line x1="12" y1="18" x2="12.01" y2="18"></line>
        </svg>
      );
    }
    return <span className="text-xl font-bold">{methodName?.charAt(0)?.toUpperCase() || '?'}</span>;
  };

  if (hasError) {
    return (
      <div className={`${sizeClasses} shrink-0 rounded-full flex items-center justify-center shadow-sm border ${isSelected ? 'bg-blue-900 text-white border-blue-900' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
        {getFallbackIcon()}
      </div>
    );
  }

  return (
    <div className={`${sizeClasses} shrink-0 rounded-full flex items-center justify-center overflow-hidden bg-white shadow-sm border transition-colors ${isSelected ? 'border-blue-900' : 'border-slate-200'}`}>
      <img src={imagePath} alt={`Logo de ${methodName}`} className="w-full h-full object-cover" onError={() => setHasError(true)} />
    </div>
  );
};

const PaymentDestinationScreen = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const { transactionId } = location.state || {};
  const [transaction, setTransaction] = useState(null);
  const [userMethods, setUserMethods] = useState([]);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newMethodData, setNewMethodData] = useState({
    holder_name: "",
    account_number: "",
    bank: "",
    account_type: "",
    document_type: "",
    document_number: ""
  });

  useEffect(() => {
    const fetchTransaction = async () => {
      if (!transactionId || !user?.id) return;
      const { data } = await scopedTransactionSelect(supabase, transactionId, user.id).single();
      if (data) setTransaction(data);  
    };  
    fetchTransaction();
  }, [transactionId, user?.id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!transaction || !user) return;

      const { data: userMethodsData } = await supabase  
        .from("user_payment_methods")  
        .select("*")  
        .eq("user_id", user.id)  
        .eq("country_code", transaction.destination_country)
        .order("created_at", { ascending: false });

      if (userMethodsData) setUserMethods(userMethodsData);  

      const { data: configData } = await supabase
        .from("destination_methods_config")
        .select("*")
        .eq("country_code", transaction.destination_country)
        .eq("active", true) 
        .order("created_at", { ascending: true });

      if (configData) setAvailableConfigs(configData);

      setLoading(false);  
    };  

    fetchData();
  }, [transaction, user]);

  const handleSelectConfig = (config) => {
    setSelectedConfig(config);
    setNewMethodData({ holder_name: "", account_number: "", bank: "", account_type: "", document_type: "", document_number: "" });
  };

  const handleAddNewMethod = async () => {
    if (!transaction || !user || !selectedConfig) return;

    if (!newMethodData.holder_name || !newMethodData.document_type || !newMethodData.document_number || !newMethodData.account_number) {
      return alert("Completa todos los campos obligatorios.");
    }

    const insertData = {  
      user_id: user.id,  
      country_code: transaction.destination_country,  
      method: sanitizeText(selectedConfig.method_name, { fieldName: 'Método', maxLength: 80 }),  
      account_type: selectedConfig.requires_account_type
        ? sanitizeText(newMethodData.account_type, { fieldName: 'Tipo de cuenta', maxLength: 40 })
        : null,  
      data: {  
        holder_name: validatePersonName(newMethodData.holder_name, 'Titular'),  
        account_number: sanitizeText(newMethodData.account_number, { fieldName: 'Cuenta', maxLength: 40 }),  
        bank: selectedConfig.requires_bank
          ? sanitizeText(newMethodData.bank, { fieldName: 'Banco', maxLength: 80 })
          : null,  
        document_type: sanitizeText(newMethodData.document_type, { fieldName: 'Tipo de documento', maxLength: 20 }),  
        document_number: sanitizeText(newMethodData.document_number, { fieldName: 'Documento', maxLength: 30 })  
      }  
    };  

    const { data, error } = await supabase.from("user_payment_methods").insert([insertData]).select().single();  

    if (!error) {
      setUserMethods(prev => [data, ...prev]);
      setShowMethodModal(false);
      setSelectedConfig(null);
    } else {
      alert("Error al guardar el método.");
    }
  };

  const handleContinue = async () => {
    if (!selectedSavedMethod || !transaction) return;
    await scopedTransactionUpdate(supabase, transaction.id, user.id, {
      payment_method_destination_id: parseUuid(selectedSavedMethod.id, 'Método de destino'),
      current_step: "payment_origin"
    });
    navigate("/send/step4", { state: { transactionId: transaction.id } });
  };

  if (loading) return (
    <Layout showNavigation={false}>
      <div className="min-h-screen flex items-center justify-center bg-[#f4f7fb]">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </Layout>
  );

  return (
    <Layout showNavigation={false}>
      <div className="min-h-screen bg-[#f4f7fb] px-4 py-10">
        <div className="mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Método de recepción</h1>
            <p className="text-slate-500 font-medium">Destino: <span className="text-blue-600 font-bold">{transaction?.destination_country}</span></p>
          </motion.div>

          {/* MÉTODOS GUARDADOS DEL USUARIO CON TODA LA DATA */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {userMethods.map((m) => {
              const isSelected = selectedSavedMethod?.id === m.id;
              return (
                <motion.div key={m.id} whileTap={{ scale: 0.98 }} onClick={() => setSelectedSavedMethod(m)} 
                  className={`p-5 rounded-[32px] border-2 cursor-pointer transition-all flex flex-col gap-4 ${isSelected ? "border-blue-600 bg-blue-50/50 shadow-xl" : "border-transparent bg-white shadow-sm hover:bg-slate-50"}`}>
                  
                  <div className="flex gap-4 items-center">
                    <MethodLogo methodName={m.method} isSelected={isSelected} size="large" />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <p className={`font-black text-lg leading-tight uppercase tracking-tighter ${isSelected ? 'text-blue-900' : 'text-slate-800'}`}>{m.method}</p>
                        {isSelected && <div className="bg-blue-600 text-white rounded-full p-1"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg></div>}
                      </div>
                      <p className="text-slate-700 font-bold text-sm uppercase">{m.data?.holder_name}</p>
                    </div>
                  </div>

                  <div className="bg-white/60 rounded-2xl p-3 space-y-1 border border-slate-100/50">
                    <div className="flex justify-between text-[11px] font-black uppercase text-slate-400">
                      <span>{m.method.includes('vile') ? 'Teléfono' : 'Cuenta'}:</span>
                      <span className="text-slate-800 font-mono tracking-tighter">{m.data?.account_number}</span>
                    </div>
                    
                    {m.data?.bank && (
                      <div className="flex justify-between text-[11px] font-black uppercase text-slate-400">
                        <span>Banco:</span>
                        <span className="text-blue-600">{m.data.bank}</span>
                      </div>
                    )}
                    
                    {m.account_type && (
                      <div className="flex justify-between text-[11px] font-black uppercase text-slate-400">
                        <span>Tipo:</span>
                        <span className="text-slate-600 italic">{m.account_type}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-[11px] font-black uppercase text-slate-400">
                      <span>Doc:</span>
                      <span className="text-slate-600">{m.data?.document_type} {m.data?.document_number}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="space-y-4">
            <button onClick={() => setShowMethodModal(true)} className="w-full py-4 bg-white border-2 border-dashed border-slate-300 rounded-3xl font-bold text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
              <span className="text-xl">+</span> Agregar nuevo método
            </button>
            <button onClick={handleContinue} disabled={!selectedSavedMethod} className={`w-full py-4 rounded-3xl font-black text-lg transition-all ${selectedSavedMethod ? "bg-blue-600 text-white shadow-xl shadow-blue-200" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
              Continuar
            </button>
          </div>
        </div>

        {/* MODAL DINÁMICO */}
        {showMethodModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-0 sm:p-4">
            <motion.div initial={{ y: 100 }} animate={{ y: 0 }} className="bg-white rounded-t-[40px] sm:rounded-[40px] w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Nuevo método</h2>
                <button onClick={() => setShowMethodModal(false)} className="p-2 bg-slate-100 rounded-full text-slate-400">✕</button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">1. Selecciona Plataforma</label>
                  <div className="grid grid-cols-3 gap-2">
                    {availableConfigs.map((config) => (
                      <button key={config.id} onClick={() => handleSelectConfig(config)} className={`p-3 rounded-2xl border-2 transition-all flex flex-col items-center ${selectedConfig?.id === config.id ? 'border-blue-600 bg-blue-50' : 'border-slate-50'}`}>
                        <MethodLogo methodName={config.method_name} isSelected={selectedConfig?.id === config.id} size="small" />
                        <span className="text-[10px] font-bold mt-2 text-center leading-tight">{config.method_name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {selectedConfig && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                    <input type="text" placeholder="Nombre completo del titular" className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={newMethodData.holder_name} onChange={e => setNewMethodData({...newMethodData, holder_name: e.target.value})} />
                    
                    <div className="flex gap-2">
                      <select className="w-1/3 p-4 bg-slate-50 rounded-2xl outline-none font-bold" value={newMethodData.document_type} onChange={e => setNewMethodData({...newMethodData, document_type: e.target.value})}>
                        <option value="">Tipo</option><option value="V">V</option><option value="E">E</option><option value="CC">CC</option><option value="PAS">PAS</option><option value="J">J</option>
                      </select>
                      <input type="text" placeholder="N° Documento" className="w-2/3 p-4 bg-slate-50 rounded-2xl outline-none font-bold" value={newMethodData.document_number} onChange={e => setNewMethodData({...newMethodData, document_number: e.target.value})} />
                    </div>

                    <input type="text" placeholder={selectedConfig.account_number_placeholder} className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-bold" value={newMethodData.account_number} onChange={e => setNewMethodData({...newMethodData, account_number: e.target.value})} />

                    {selectedConfig.requires_bank && (
                      <select className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-bold" value={newMethodData.bank} onChange={e => setNewMethodData({...newMethodData, bank: e.target.value})}>
                        <option value="">Selecciona Banco</option>
                        {selectedConfig.bank_options?.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    )}

                    {selectedConfig.requires_account_type && (
                      <select className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-bold" value={newMethodData.account_type} onChange={e => setNewMethodData({...newMethodData, account_type: e.target.value})}>
                        <option value="">Tipo de cuenta</option><option value="ahorro">Ahorro</option><option value="corriente">Corriente</option>
                      </select>
                    )}
                  </motion.div>
                )}
              </div>

              <div className="p-6 bg-white border-t">
                <button onClick={handleAddNewMethod} disabled={!selectedConfig} className={`w-full py-4 rounded-2xl font-black transition-all ${selectedConfig ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-300'}`}>Guardar método</button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PaymentDestinationScreen;

